import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { uniqBy } from 'lodash';

import Strings from '../../../Strings';
import { canEditReminderSource, reminderConfigurationRows, reminderSourceActions } from '../../../utils/reminderConfiguration';
import { supportedLanguages } from '../../../utils/language';
import Table3, { columnHelper } from '../../../containers/Table/Table3';
import { PageHeader } from '../../../components/PageHeader';
import { TextInput } from '../../../components/PageHeader/TextInput';
import AdvancedFilters, { Filter } from '../../../components/AdvancedFilters/AdvancedFilters';
import { getFiltersDefinition } from '../../../components/AdvancedFilters/FiltersDefinition';
import AdvancedFiltersBar from '../../../components/AdvancedFilters/AdvancedFiltersBar';
import { ActionButton, ActionWidget } from '../../../components/ActionWidget';
import { openModalAction } from '../../../actions/modal';
import { getRelevantFilters } from '../../../components/AdvancedFilters/helpers';
import { hasPermission, PERMISSIONS } from '../../../utils/userPermissions';
import { showStatus, toolTipForTable } from '../../../utils';
import { isSuperUser } from '../../../utils/userRoles';
import { actions } from '../redux/actions';

export const getComplianceText = (value) => {
  let postFix = "";
  if (value?.endsWith('phi_safe')) {
    postFix = " (PHI Safe)";
  }
  const v = value?.replace('_phi_safe', '') || '';
  switch (v) {
    case 'compliant':
      return `compliant${postFix}`;
    case 'non_compliant':
      return `non-compliant${postFix}`;
    case 'partial':
      return `partially compliant${postFix}`;
    default:
      return value;
  }
};

export const reminderSourceLabel = source => {
  if (!source) return Strings.reminderNotConfigured;
  const scope =
    source.type === 'global'
      ? Strings.globalDefaults
      : source.org_id === null
      ? Strings.restrictedOrganization
      : `${Strings.orgLevels?.[source.type] || source.type}: ${source.name}`;
  return Strings.formatString(
    Strings.sourceLanguage,
    scope,
    Strings.languagesEnum?.[source.language?.toLowerCase()] || source.language,
  );
};

function DefaultMessages(props) {
  const { getConfiguration, editReminders, filters, organizationId } = props;
  const [data, setData] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [selectedReminders, setSelectedReminders] = React.useState([]);
  const [searchQuery, setSearchQuery] = React.useState('');

  const tableRef = React.useRef(null);
  const canEdit = hasPermission(PERMISSIONS.MESSAGES_DEFAULT_REMINDERS_EDIT) || isSuperUser();

  const getDefaultMessages = async () => {
    setLoading(true);
    let ret = [];
    for (const [index, language] of supportedLanguages.map(l => l.id).entries()) {
      const messagesResp = await getConfiguration(organizationId, language);
      ret.push(...reminderConfigurationRows(messagesResp?.response, language));
      if (messagesResp?.response?.time_milestone_messages) {
        Object.entries(messagesResp.response.time_milestone_messages?.value || {}).forEach(([days, messages]) => {
          Object.entries(messages).forEach(([type, message]) => {
            const isPhiSafe = type?.endsWith('phi_safe') || false;
            ret.push({
              type: `Adherence milestone SMS after ${days} day(s) ${getComplianceText(type)}`,
              message,
              hasOverride: messagesResp.response.time_milestone_messages?.own_value != null,
              language,
              editedLanguage: messagesResp.response.time_milestone_messages?.source?.language,
              source: messagesResp.response.time_milestone_messages?.source,
              canEditSource: canEditReminderSource(messagesResp.response.time_milestone_messages?.source, messagesResp.response.ancestors),
              days: days,
              timeMessageFor: type,
              isPhiSafe: isPhiSafe,
              actions: messagesResp.response.time_milestone_messages?.actions.filter(a => (a.supportedInPhiSafe === isPhiSafe) || !isPhiSafe) || [],
            });
          });
        });
      }
    }
    setData(ret);
    setLoading(false);
  }
  React.useEffect(() => {
    getDefaultMessages();
  }, [organizationId]);

  const columns = React.useMemo(() => [
    columnHelper.accessor('type', {
      header: Strings.type,
      cell: info => Strings.defaultRemindersEnum[info.getValue()] || info.getValue(),
      size: 260,
    }),
    columnHelper.accessor('language', {
      header: Strings.language,
      size: 150,
    }),
    columnHelper.accessor('message', {
      header: Strings.message,
      cell: info => toolTipForTable(info.getValue(), info.getValue()),
      size: 1000,
    }),
    columnHelper.accessor('isPhiSafe', {
      header: Strings.isPhiSafe,
      cell: info => showStatus(info.getValue()),
      size: 130,
    }),
    columnHelper.accessor('source', {
      header: Strings.templateSource,
      cell: info => reminderSourceLabel(info.getValue()),
      size: 200,
    }),
    columnHelper.accessor('tags', {
      header: Strings.tags,
      cell: info => {
        const actionsText = (info.getValue() || []).map(a => a.tag);
        return toolTipForTable(actionsText.join(', '), actionsText);
      },
      accessorFn: row => row?.actions,
      size: 200,
    }),
  ], []);
  
  const buttons = React.useMemo(() => [
    {
      icon: 'edit',
      visible: (_, reminder) => !!reminder.source,
      disabled: (_, reminder) => !reminderSourceActions(reminder, organizationId, !organizationId, isSuperUser()).canEditSource,
      disabledTooltip: Strings.insufficientPermissionsToEditTemplate,
      onClick: (_, reminder) => {
        const sourceActions = reminderSourceActions(reminder, organizationId, !organizationId, isSuperUser());
        if (!sourceActions.canEditSource) return;
        editReminders({
          reminders: [reminder],
          orgId: sourceActions.sourceOrgId,
          onSuccess: getDefaultMessages
        });
      },
      text: (_, reminder) => Strings.formatString(Strings.editCurrentlyUsedTemplate, reminderSourceLabel(reminder.source) || '???'),
    },
    {
      icon: 'plus',
      onClick: (_, reminder) => editReminders({
        reminders: [{...reminder, editedLanguage: reminder.language}],
        orgId: organizationId,
        onSuccess: getDefaultMessages
      }),
      visible: (_, reminder) => reminderSourceActions(reminder, organizationId, !organizationId).canCreateOverride,
      text: organizationId ? Strings.createOrgSpecificTemplate : Strings.createLanguageSpecificTemplate,
    },
    {
      icon: 'delete',
      onClick: (_, reminder) => editReminders({
        reminders: [{ ...reminder, editedLanguage: reminder.language }],
        orgId: organizationId,
        clearing: true,
        onSuccess: getDefaultMessages
      }),
      visible: (_, reminder) => reminderSourceActions(reminder, organizationId, !organizationId).canClearOverride,
      text: organizationId ? Strings.clearOrgSpecificTemplate : Strings.clearLanguageSpecificTemplate,
    }
  ]);

  const actionBtns = (
    <ActionButton
      img="action-edit"
      tooltiptext={Strings.editDefaultReminders}
      text={Strings.editDefaultReminders}
      action={() => editReminders({
        reminders: selectedReminders.map(reminder => ({ ...reminder, editedLanguage: reminder.language })),
        orgId: organizationId,
        onSuccess: () => {
          getDefaultMessages();
          tableRef?.current?.resetSelectedRows();
        }
      })}
    />
  );

  const onSearchQueryChange = _.debounce (query => {
    setSearchQuery(query);
  }, 200);

  const dataForTable = React.useMemo(() => {
    let ret = [...data] || [];
    if (!data?.length) {
      return [];
    }
    if (searchQuery) {
      ret = data.filter(r => {
        const language = r.language?.toLowerCase() || '';
        const type = r.type?.toLowerCase() || '';
        const message = r.message?.toLowerCase() || '';
        return language.includes(searchQuery.toLowerCase()) ||
          type.includes(searchQuery.toLowerCase()) ||
          message.includes(searchQuery.toLowerCase());
      });
    }
    const appliedFilters = getRelevantFilters(filters);
    if (appliedFilters?.length === 0) {
      return ret;
    }

    const hasLanguageFilter = appliedFilters.some(f => f.group === 'language');
    const hasTypeFilter = appliedFilters.some(f => f.group === 'remindersType');
    const hasPhiSafeFilter = appliedFilters.some(f => f.group === 'phiSafe');

    return ret.filter(r => {
      const matchesLanguage =
        !hasLanguageFilter ||
        appliedFilters.some(f => f.group === 'language' && f.value === r.language);

      const matchesType =
        !hasTypeFilter ||
        appliedFilters.some(
          f =>
            (f.group === 'remindersType' && f.value === r.type) ||
            (f.group === 'remindersType' && f.value === 'adherence_milestones' && r.type.startsWith('Adherence milestone')),
        );

      const matchesPhiSafe =
        !hasPhiSafeFilter ||
        appliedFilters.some(f => f.group === 'phiSafe' && f.value === String(r.isPhiSafe));

      return matchesLanguage && matchesType && matchesPhiSafe;
    });
  }, [data, searchQuery, filters]);

  const sourceTypes = uniqBy(
    data.filter(r => r.source).map(r => r.source),
    source => JSON.stringify({
      type: source.type,
      org_id: source.org_id,
      name: source.name,
      language: source.language,
    })
  );

  return (
    <>
      <PageHeader
        right={(
          <>
            <TextInput placeholder={Strings.search} onChange={e => onSearchQueryChange(e.target.value)} />
            <AdvancedFilters>
              <Filter definition={getFiltersDefinition().language} />
              <Filter definition={getFiltersDefinition().remindersType} />
              <Filter definition={getFiltersDefinition().phiSafe} />
            </AdvancedFilters>
          </>
        )}
      />
      <AdvancedFiltersBar />
      <Table3
        data={dataForTable}
        isLoading={loading}
        columns={columns}
        name="ShippingRequests"
        buttons={canEdit ? buttons : undefined}
        enableColumnFiltering
        enableRowSelection={canEdit}
        pinFirstColumn
        onRowSelectionChange={setSelectedReminders}
        grouping={["language"]}
        ref={tableRef}
      />
      {canEdit && <ActionWidget show={selectedReminders?.length > 0}>{actionBtns}</ActionWidget>}
    </>
  );
}

DefaultMessages.propTypes = {
  getConfiguration: PropTypes.func.isRequired,
  editReminders: PropTypes.func.isRequired,
  filters: PropTypes.arrayOf(PropTypes.object).isRequired,
  organizationId: PropTypes.string.isRequired,
};

const mapStateToProps = state => {
  return {
    filters: state.entities.advancedFilters.filters.items,
  };
};

const mapDispatchToProps = dispatch => ({
  getConfiguration: (orgId, language) => dispatch(actions.getReminderConfiguration(orgId, language)),
  editReminders: data => dispatch(openModalAction('edit-default-reminders', data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(DefaultMessages);
